// Dated things, normalised across feeds.
//
// The map has two feeds of events today — GAA matches (data/gaa-fixtures.json) and
// horse racing + eventing (data/equine-events.json) — and they answer the same
// traveler's question: "what is on while I'm there?". So both are adapted into one
// EventItem shape, and the date-range filter, the map rings and the "What's on" list
// work on that alone.
//
// To add a third feed: give it an entry in FEEDS, an adapter to EventItem, load it in
// data/loader.ts and add it to `allEvents` in state/store.ts. Nothing else changes.
import type { EquineEvent, EventFeed, EventItem, Fixture, LayerId } from "../types";
import { LAYERS, disciplineLabel, equineKindLabel } from "./layers";

export const FEED_ORDER: EventFeed[] = ["gaa", "equine"];

export interface FeedDef {
  id: EventFeed;
  /** Name in the feed chips. */
  label: string;
  /** Where this feed's venues live, so a ring can open the place's own page. */
  layer: LayerId;
  color: string;
  /** Heading for the first facet row, and its labels. */
  categoryTitle: string;
  categoryLabel: (id: string) => string;
  /** Heading for the second facet row, and its labels. */
  bandTitle: string;
  bandLabel: (id: string) => string;
  /** The word for one of these, singular and plural. */
  noun: [string, string];
}

const GAA_CODE_LABELS: Record<string, string> = {
  football: "Football", hurling: "Hurling", camogie: "Camogie", ladies_football: "Ladies football",
};
const GAA_LEVEL_LABELS: Record<string, string> = {
  inter_county: "County teams", club: "Clubs", other: "Other",
};

export const FEEDS: Record<EventFeed, FeedDef> = {
  gaa: {
    id: "gaa",
    label: "Gaelic games",
    layer: "gaa",
    color: LAYERS.gaa.color,
    categoryTitle: "Code",
    categoryLabel: (c) => GAA_CODE_LABELS[c] ?? c,
    bandTitle: "Level",
    bandLabel: (l) => GAA_LEVEL_LABELS[l] ?? l,
    noun: ["match", "matches"],
  },
  equine: {
    id: "equine",
    label: "Horses",
    layer: "equine",
    color: LAYERS.equine.color,
    categoryTitle: "Kind",
    categoryLabel: equineKindLabel,
    bandTitle: "Discipline",
    bandLabel: disciplineLabel,
    noun: ["fixture", "fixtures"],
  },
};

/** Facet keys are feed-qualified, so "point_to_point" as an equine kind never collides
 *  with "point_to_point" as a discipline, and a GAA chip never hides a race. */
export const facetKey = (feed: EventFeed, id: string): string => `${feed}:${id}`;

export function fixtureToEvent(f: Fixture): EventItem {
  return {
    key: `gaa:${f.id}`,
    feed: "gaa",
    id: f.id,
    date: f.date,
    endDate: f.range?.[1] ?? f.date,
    time: f.time ?? null,
    title: `${f.home} v ${f.away}`,
    category: f.code,
    band: f.level,
    venue: { name: f.venue.name, lat: f.venue.lat, lon: f.venue.lon, county: f.venue.county ?? f.county ?? null, approx: f.venue.approx, feature: f.venue.ground },
    raw: f,
  };
}

export function equineToEvent(e: EquineEvent): EventItem {
  return {
    key: `equine:${e.id}`,
    feed: "equine",
    id: e.id,
    date: e.date,
    endDate: e.end_date || e.date,
    time: e.time ?? null,
    title: e.title,
    category: e.kind,
    band: e.discipline,
    venue: { name: e.venue.name, lat: e.venue.lat, lon: e.venue.lon, county: e.venue.county ?? e.county ?? null, approx: e.venue.approx, feature: e.venue.place },
    raw: e,
  };
}

/** Inclusive overlap of an event's run with a [from, to] window. */
export function inWindow(e: EventItem, from: string, to: string): boolean {
  return e.date <= to && e.endDate >= from;
}
